/**
 * Shared API Client
 * Configurable API client for the shared package
 * 
 * Supports three authentication methods:
 * 1. JWT Token (admin access): Authorization: Bearer eyJ...
 * 2. API Token (full tenant access): Authorization: Token emg_xxx
 * 3. Public Key (read-only): X-Public-Key: pub_xxx
 * 
 * Token type is auto-detected based on format:
 * - JWT tokens start with "eyJ"
 * - API tokens start with "emg_"
 * 
 * IMPORTANT: For essay/speaking AI grading in single-player mode, the configured
 * token must have `job.read` permission. A view-token (JWT with only `content.read`
 * + `session.read`) will fail job polling with 403 permission_denied. Use an API
 * token (emg_xxx) which includes `job.read` by default, or a JWT with full permissions.
 */

import { normalizeEnvelopeErrorDetails } from './fieldErrors';

export interface SharedApiConfig {
  baseUrl: string;
  h5pUrl: string;
  /**
   * Base URL of the STATIC H5P bundle (`apps/h5p-core`), e.g. `/h5p-static`
   * or `https://cdn.example.com/h5p-static`. When set, player models are read
   * from `<base>/manifests/<Ubername>.json` instead of the H5P server's
   * `GET /h5p/new/play`, so playback needs no H5P service and no second domain.
   *
   * `h5pUrl` stays in place as the fallback (and is still required for the
   * EDITOR, which the static bundle does not cover).
   */
  h5pStaticBase?: string;
  /** 
   * Auth token for API access
   * - JWT tokens (eyJ...): sent as "Bearer {token}"
   * - API tokens (emg_...): sent as "Token {token}"
   */
  getAuthToken?: () => string | null;
  /** Public key for read-only access (format: pub_xxx) */
  getPublicKey?: () => string | null;
  timeout?: number;
  /**
   * Called after a successful HTTP response (2xx) from the main Content API (baseUrl).
   * Used by web-content embed (/embed/*) to postMessage the parent LMS for taxonomy sync.
   */
  onEmbedApiSuccess?: (payload: {
    method: string;
    endpoint: string;
    status: number;
  }) => void;
  /**
   * Return the primary language tag for `Accept-Language` (e.g. `en` or `vi`).
   * When set, overrides the browser default so API error `description` / `details.message`
   * match the app UI language.
   */
  getAcceptLanguage?: () => string;
  /**
   * Embed content session JWT: Authorization: Session <jwt>.
   * When this returns a non-empty string, it takes precedence over getAuthToken.
   */
  getSessionToken?: () => string | null;
  /**
   * EAA-1682: called on a 401 from the main Content API so the caller can refresh
   * the JWT access token (e.g. `authService.refreshToken()`), resolving to the new
   * access token or `null`/`undefined` if the refresh failed (session truly expired).
   * Without this, requests through `sharedApiClient` never retried after a 401 —
   * unlike `apps/web-content`'s own `apiClient`, which already refreshes-and-retries —
   * so tag/content/program calls made from a long-idle tab failed outright once the
   * access token expired instead of transparently refreshing it. Only consulted for
   * getAuthToken-based (JWT) requests, not session/public-key embed auth.
   */
  onUnauthorized?: () => Promise<string | null | undefined>;
  /**
   * EAA-1694: the acting ACH tenant, sent as `X-Tenant-Id`.
   *
   * An SSO identity can hold memberships in several tenants (its org may be mapped to
   * more than one), and the backend refuses to guess: with more than one membership and
   * no header there is no acting tenant, so the caller has no permissions and every
   * request 403s. This is how an app names which one it is acting in.
   *
   * Only meaningful for SSO bearer auth. API-token and public-key callers carry their
   * tenant in the credential itself, and a session (embed) JWT is already scoped, so the
   * header is simply omitted when this returns nothing.
   */
  getActingTenantId?: () => string | null;
}

export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  ok: boolean;
}

/** Envelope returned by the backend for all API responses. */
export interface ApiEnvelope<T = unknown> {
  success: boolean;
  code: number;
  message: string;
  description: string;
  data: T;
  /** Raw validation details (string or structured `{ code, message }` per item). */
  details?: Record<string, unknown>;
}

export interface ApiError {
  message: string;
  description?: string;
  status?: number;
  code?: string;
  errorCode?: number;
  details?: Record<string, string[]>;
}

export interface MultipartUploadProgress {
  percent: number;
  loaded: number;
  total: number;
}

export interface PostFormDataOptions {
  onUploadProgress?: (progress: MultipartUploadProgress) => void;
  signal?: AbortSignal;
  /** Defaults to SharedApiConfig.timeout (often too low for large files). */
  timeoutMs?: number;
}

export interface PostJsonOptions {
  /** Override SharedApiConfig.timeout for long-running jobs (e.g. bulk AI generate). */
  timeoutMs?: number;
}

let apiConfig: SharedApiConfig | null = null;

function stripTrailingSlashes(url: string): string {
  return url.replace(/\/+$/, '');
}

/**
 * Content Hub REST routes are mounted under `/api`. Callers often pass the
 * origin only (e.g. `http://localhost:8000` from LMS launch params). Without
 * this, requests go to `/content/...` and the server returns 404 (envelope
 * code 3000) instead of `/api/content/...`.
 */
export function ensureContentApiBaseUrl(baseUrl: string): string {
  const t = stripTrailingSlashes(baseUrl.trim());
  if (/\/api$/i.test(t)) {
    return t;
  }
  return `${t}/api`;
}

export function configureApi(config: SharedApiConfig): void {
  const rawBase = stripTrailingSlashes(config.baseUrl.trim());
  const baseUrl = ensureContentApiBaseUrl(config.baseUrl);
  const explicitH5p = (config.h5pUrl && config.h5pUrl.trim()) || '';
  const h5pLooksUnset =
    !explicitH5p || stripTrailingSlashes(explicitH5p) === rawBase;
  const h5pUrl = h5pLooksUnset
    ? baseUrl.replace(/\/api\/?$/i, '/h5p')
    : explicitH5p;
  apiConfig = {
    ...config,
    baseUrl,
    h5pUrl,
    h5pStaticBase: config.h5pStaticBase
      ? stripTrailingSlashes(config.h5pStaticBase.trim()) || undefined
      : undefined,
    timeout: config.timeout ?? 30000,
  };

  if (typeof window !== 'undefined') {
    const w = window as unknown as {
      __SHARED_API_AUTH_HEADERS__?: () => Record<string, string>;
    };
    w.__SHARED_API_AUTH_HEADERS__ = () => getSharedApiAuthHeaders();
  }
}

export function getApiConfig(): SharedApiConfig {
  if (!apiConfig) {
    throw new Error(
      'Shared API not configured. Call configureApi() or wrap your app with SharedConfigProvider.'
    );
  }
  return apiConfig;
}

export function isApiConfigured(): boolean {
  return apiConfig !== null;
}

/**
 * Detect token type and return appropriate auth header prefix
 */
function getAuthPrefix(token: string): 'Bearer' | 'Token' {
  // JWT tokens start with "eyJ" (base64 encoded JSON header)
  if (token.startsWith('eyJ')) {
    return 'Bearer';
  }
  // API tokens start with "emg_"
  if (token.startsWith('emg_')) {
    return 'Token';
  }
  // Default to Bearer for unknown token formats (assume JWT)
  return 'Bearer';
}

function isH5pServerBase(baseUrl: string): boolean {
  if (!apiConfig?.h5pUrl) {
    return false;
  }
  return stripTrailingSlashes(baseUrl) === stripTrailingSlashes(apiConfig.h5pUrl);
}

interface GetAuthHeadersOptions {
  /**
   * H5P server (e.g. h5p-server.vmb.edu.vn) must not receive app/content-type version headers.
   */
  omitVersionHeaders?: boolean;
  /**
   * EAA-1694: the H5P server (h5p-server.vmb.edu.vn) is a THIRD-PARTY origin outside ACH's
   * tenant model, and its CORS config was never asked to allow X-Tenant-Id — sending it
   * unconditionally (as EAA-1694's own tenant-header work did, before this flag existed)
   * turned every H5P editor/player model fetch into a CORS failure: the preflight OPTIONS
   * still answers 204 (it just does not echo the header back as allowed), so the browser
   * blocks the real GET/POST and no response ever reaches the app. That surfaced as
   * "Error loading H5P content from server: Failed to fetch" on `fetchH5PEditorModel` /
   * `fetchH5PPlayerModel` in content.service.ts, and would hit any upload whose base is the
   * H5P server too.
   *
   * Deliberately its OWN flag, not reused from `omitVersionHeaders` — those two happen to
   * always be set together today (every caller of this function that skips version headers
   * is, in fact, targeting the H5P server), but they are different questions. A future
   * caller that legitimately wants to skip version headers against the ACH API would, with
   * a shared flag, silently lose the tenant header too — and that fails as a 403 that reads
   * like a permissions bug, the same class of failure this ticket exists to fix.
   */
  omitTenantHeader?: boolean;
  /**
   * EAA-1694: multipart XHR uploads (ImageUpload/DocumentUpload/content-api's
   * setUploadHeadersFromConfig) must not carry `Content-Type: application/json` — the
   * browser needs to set its own `multipart/form-data; boundary=...` value, and a fixed
   * JSON content-type here breaks the upload. Callers building upload headers pass this
   * instead of building the header set by hand and deleting the key afterwards, so the
   * single header-assembly function stays the one place auth/tenant headers are decided.
   */
  omitContentType?: boolean;
}

/**
 * Headers for H5P server requests only (no X-App-Version / X-Content-Type-Versions, no
 * X-Tenant-Id — see omitTenantHeader's doc comment).
 * Use for fetch() to the configured h5pUrl.
 */
export function getH5pRequestHeaders(): HeadersInit {
  return getAuthHeaders({ omitVersionHeaders: true, omitTenantHeader: true });
}

/**
 * Snapshot of HTTP headers used for Content Hub API calls (matches sharedApiClient).
 * Prefer this for custom fetch() (e.g. media signed-url) plus optional
 * ``window.__UPLOAD_GET_HEADERS__`` for XMLHttpRequest uploads.
 */
export function getSharedApiAuthHeaders(
  options?: Pick<GetAuthHeadersOptions, 'omitVersionHeaders'>
): Record<string, string> {
  if (!apiConfig) {
    return {};
  }
  return { ...(getAuthHeaders(options) as Record<string, string>) };
}

/**
 * EAA-1694: headers for a multipart XHR upload (ImageUpload/DocumentUpload and
 * content-api's window.__UPLOAD_GET_HEADERS__ bridge). Media uploads never went through
 * `sharedApiClient`/`getAuthHeaders` — they hand-built `{ Authorization }` only — so a
 * caller with more than one tenant membership had no `X-Tenant-Id` on the request and the
 * backend resolved zero permissions, 403-ing every upload. This returns the same
 * auth/tenant/language headers as `getSharedApiAuthHeaders` but never sets `Content-Type`,
 * since a multipart body must declare its own boundary.
 */
export function getSharedApiUploadHeaders(): Record<string, string> {
  if (!apiConfig) {
    return {};
  }
  return {
    ...(getAuthHeaders({ omitContentType: true }) as Record<string, string>),
  };
}

function getAuthHeaders(options?: GetAuthHeadersOptions): HeadersInit {
  const headers: Record<string, string> = {};
  if (!options?.omitContentType) {
    headers['Content-Type'] = 'application/json';
  }

  if (!options?.omitVersionHeaders) {
    // Attach app version for backend logging (Content Hub API only)
    if (typeof window !== 'undefined' && (window as { APP_VERSION?: string }).APP_VERSION) {
      headers['X-App-Version'] = (window as { APP_VERSION?: string }).APP_VERSION!;
    }

    // Attach content-type package versions
    const ctVersions = typeof window !== 'undefined'
      ? (window as { CONTENT_TYPE_VERSIONS?: Record<string, string> }).CONTENT_TYPE_VERSIONS
      : undefined;
    if (ctVersions) {
      headers['X-Content-Type-Versions'] = JSON.stringify(ctVersions);
    }
  }

  // Session JWT (embed) takes precedence over API/JWT token
  let authFromSession = false;
  if (apiConfig?.getSessionToken) {
    const sessionTok = apiConfig.getSessionToken();
    if (sessionTok) {
      headers['Authorization'] = `Session ${sessionTok}`;
      authFromSession = true;
    }
  }
  if (!authFromSession && apiConfig?.getAuthToken) {
    const token = apiConfig.getAuthToken();
    if (token) {
      const prefix = getAuthPrefix(token);
      headers['Authorization'] = `${prefix} ${token}`;
    }
  }

  // Public Key authentication (read-only access)
  if (apiConfig?.getPublicKey) {
    const publicKey = apiConfig.getPublicKey();
    if (publicKey) {
      headers['X-Public-Key'] = publicKey;
    }
  }

  // EAA-1694: acting tenant for SSO identities (see getActingTenantId). Deliberately
  // sent alongside any auth scheme rather than only the bearer one — the backend ignores
  // it for credentials that already carry a tenant, and special-casing here would mean
  // guessing the scheme from the token's shape a second time.
  //
  // EAA-1694: never for the H5P server (see omitTenantHeader's doc comment) — it is a
  // third-party origin whose CORS config was never asked to allow this header, so sending
  // it unconditionally turned every H5P editor/player model fetch into a CORS failure.
  if (!options?.omitTenantHeader && apiConfig?.getActingTenantId) {
    const actingTenantId = apiConfig.getActingTenantId();
    if (actingTenantId) {
      headers['X-Tenant-Id'] = actingTenantId;
    }
  }

  // WHY: an embed document (`/embed/ai`, `/embed/player`, …) is handed its tenant on
  // its own URL and is framed with a USER bearer token, which carries no tenant. Each
  // embed also calls `configureApi`/`configureContentApi` for itself, and that REPLACES
  // the whole config — so whichever call site forgets to forward `getActingTenantId`
  // silently drops `X-Tenant-Id` and every tenant-scoped read answers 403. Falling back
  // to the tenant in this document's own URL makes that impossible to get wrong.
  // An explicit `getActingTenantId` always wins, so the host apps are unaffected.
  //
  // EAA-2085: the fallback must honour `omitTenantHeader` too. Without that guard every
  // page carrying `?tenant_id=` (e.g. /question-bank/new?tenant_id=…) re-added the header
  // to H5P-server requests, the server's preflight rejected it, and any H5P type missing
  // from the static bundle (Speak Retell, Matching) failed with "Failed to fetch".
  if (!options?.omitTenantHeader && !headers['X-Tenant-Id'] && typeof window !== 'undefined') {
    try {
      const qs = new URLSearchParams(window.location.search);
      const urlTenantId = qs.get('tenant_id') || qs.get('tenantId');
      if (urlTenantId) {
        headers['X-Tenant-Id'] = urlTenantId;
      }
    } catch {
      /* ignore — a malformed query string must not break every request */
    }
  }

  if (apiConfig?.getAcceptLanguage) {
    const lang = apiConfig.getAcceptLanguage().trim();
    if (lang) {
      headers['Accept-Language'] = lang;
    }
  }

  return headers;
}

function parseApiErrorFromEnvelope(status: number, errorData: unknown): ApiError {
  let errorMessage = `HTTP error! status: ${status}`;
  let errorCode: number | undefined;
  let errorCodeStr: string | undefined;
  let description: string | undefined;
  let details: Record<string, string[]> | undefined;
  if (
    errorData &&
    typeof errorData === 'object' &&
    typeof (errorData as ApiEnvelope).code === 'number' &&
    typeof (errorData as { message?: unknown }).message === 'string'
  ) {
    const ed = errorData as Partial<ApiEnvelope> & { details?: unknown };
    errorCodeStr = ed.message;
    errorCode = ed.code;
    description = ed.description;
    errorMessage = ed.description || ed.message || errorMessage;
    details = normalizeEnvelopeErrorDetails(ed.details);
    if (details && errorCode === 2000) {
      const firstKey = Object.keys(details)[0];
      const firstMsg = firstKey ? details[firstKey]?.[0] : undefined;
      if (firstMsg) {
        errorMessage = firstMsg;
      }
    }
  } else if (errorData && typeof errorData === 'object') {
    const legacy = errorData as Record<string, string>;
    errorMessage = legacy.error || legacy.detail || errorMessage;
  }
  return {
    message: errorMessage,
    description,
    status,
    code: errorCodeStr,
    errorCode,
    details,
  };
}

async function request<T>(
  baseUrl: string,
  endpoint: string,
  options: RequestInit = {},
  requestOptions?: { timeoutMs?: number },
  isRetry = false
): Promise<ApiResponse<T>> {
  const config = getApiConfig();
  const url = `${baseUrl}${endpoint}`;

  const controller = new AbortController();
  const timeoutMs = requestOptions?.timeoutMs ?? config.timeout ?? 30000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const isH5pServer = isH5pServerBase(baseUrl);
    const response = await fetch(url, {
      ...options,
      headers: {
        ...getAuthHeaders({ omitVersionHeaders: isH5pServer, omitTenantHeader: isH5pServer }),
        ...options.headers,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.ok && config.onEmbedApiSuccess && !isH5pServerBase(baseUrl)) {
      const method = String(options.method ?? 'GET').toUpperCase();
      config.onEmbedApiSuccess({
        method,
        endpoint,
        status: response.status,
      });
    }

    // EAA-1682: a long-idle tab's access token expires, and every request
    // through sharedApiClient (tag/content/program/... services) used to
    // just fail on the resulting 401 instead of refreshing — the retry-with-
    // refreshed-token logic only existed in apps/web-content's own apiClient.
    // Session (embed) and public-key auth have no refresh token, so only
    // attempt this for the JWT (getAuthToken) path, and only once per request.
    if (
      response.status === 401 &&
      !isRetry &&
      !config.getSessionToken?.() &&
      config.onUnauthorized
    ) {
      const newToken = await config.onUnauthorized();
      if (newToken) {
        return request<T>(baseUrl, endpoint, options, requestOptions, true);
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }
    }

    if (!response.ok) {
      let error: ApiError;
      try {
        const errorData = await response.json();
        error = parseApiErrorFromEnvelope(response.status, errorData);
      } catch {
        error = { message: `HTTP error! status: ${response.status}`, status: response.status };
      }
      throw error;
    }

    if (response.status === 204) {
      return { data: {} as T, status: response.status, ok: true };
    }

    const json = await response.json() as ApiEnvelope<T> | T;
    // Unwrap envelope if present
    if (
      json !== null &&
      typeof json === 'object' &&
      'success' in json &&
      'data' in json &&
      typeof (json as ApiEnvelope<T>).code === 'number'
    ) {
      return { data: (json as ApiEnvelope<T>).data, status: response.status, ok: true };
    }
    // Legacy: raw response without envelope
    return { data: json as T, status: response.status, ok: true };
  } catch (err) {
    clearTimeout(timeoutId);

    if (err instanceof Error && err.name === 'AbortError') {
      const error: ApiError = { message: 'Request timeout', code: 'TIMEOUT' };
      throw error;
    }

    if ((err as ApiError).message) {
      throw err;
    }

    const error: ApiError = {
      message: err instanceof Error ? err.message : 'Unknown error',
    };
    throw error;
  }
}

function postFormDataWithXhr<T>(
  baseUrl: string,
  endpoint: string,
  formData: FormData,
  options?: PostFormDataOptions
): Promise<ApiResponse<T>> {
  const config = getApiConfig();
  const timeoutMs = options?.timeoutMs ?? config.timeout ?? 30000;
  const url = `${baseUrl}${endpoint}`;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const timer = globalThis.setTimeout(() => {
      xhr.abort();
      const err = new Error('Request timeout') as Error & { code?: string };
      err.code = 'TIMEOUT';
      reject(err);
    }, timeoutMs);

    const isH5pServer = isH5pServerBase(baseUrl);
    const authHeaders = getAuthHeaders({
      omitVersionHeaders: isH5pServer,
      omitTenantHeader: isH5pServer,
      omitContentType: true,
    }) as Record<string, string>;

    xhr.open('POST', url);
    Object.entries(authHeaders).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    if (options?.onUploadProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          options.onUploadProgress!({
            percent: Math.round((event.loaded / event.total) * 100),
            loaded: event.loaded,
            total: event.total,
          });
        }
      });
    }

    if (options?.signal) {
      const onAbort = () => xhr.abort();
      options.signal.addEventListener('abort', onAbort);
    }

    xhr.addEventListener('load', () => {
      globalThis.clearTimeout(timer);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const json = JSON.parse(xhr.responseText);
          // Unwrap envelope if present
          if (json && typeof json === 'object' && 'success' in json && 'data' in json && typeof json.code === 'number') {
            resolve({ data: json.data as T, status: xhr.status, ok: true });
          } else {
            resolve({ data: json as T, status: xhr.status, ok: true });
          }
        } catch {
          reject(Object.assign(new Error('Invalid JSON response'), { status: xhr.status }));
        }
        return;
      }
      let apiError: ApiError;
      try {
        const errorData = JSON.parse(xhr.responseText) as unknown;
        apiError = parseApiErrorFromEnvelope(xhr.status, errorData);
      } catch {
        apiError = { message: `HTTP error! status: ${xhr.status}`, status: xhr.status };
      }
      reject(apiError);
    });

    xhr.addEventListener('error', () => {
      globalThis.clearTimeout(timer);
      reject(new Error('Network error'));
    });

    xhr.addEventListener('abort', () => {
      globalThis.clearTimeout(timer);
      const err = new Error('Request aborted') as Error & { code?: string };
      err.code = 'ABORT';
      reject(err);
    });

    xhr.send(formData);
  });
}

/**
 * EAA-2027: GET a binary body with the caller's auth headers — for private files that
 * must be read through an authenticated endpoint rather than a shareable link. Same
 * one-shot 401 refresh and envelope error parsing as `request`.
 */
async function requestBlob(endpoint: string, isRetry = false): Promise<Blob> {
  const config = getApiConfig();
  const response = await fetch(`${config.baseUrl}${endpoint}`, {
    method: 'GET',
    headers: { ...(getAuthHeaders() as Record<string, string>) },
  });
  if (
    response.status === 401 &&
    !isRetry &&
    !config.getSessionToken?.() &&
    config.onUnauthorized
  ) {
    const newToken = await config.onUnauthorized();
    if (newToken) {
      return requestBlob(endpoint, true);
    }
  }
  if (!response.ok) {
    let error: ApiError;
    try {
      error = parseApiErrorFromEnvelope(response.status, await response.json());
    } catch {
      error = { message: `HTTP error! status: ${response.status}`, status: response.status };
    }
    throw error;
  }
  return response.blob();
}

export const sharedApiClient = {
  async get<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    const config = getApiConfig();
    return request<T>(config.baseUrl, endpoint, { method: 'GET', ...options });
  },

  /** EAA-2027: binary GET with auth headers (private files). */
  async getBlob(endpoint: string): Promise<Blob> {
    return requestBlob(endpoint);
  },

  async post<T>(
    endpoint: string,
    data?: unknown,
    options?: PostJsonOptions
  ): Promise<ApiResponse<T>> {
    const config = getApiConfig();
    return request<T>(
      config.baseUrl,
      endpoint,
      {
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
      },
      options
    );
  },

  async postFormData<T>(
    endpoint: string,
    formData: FormData,
    options?: PostFormDataOptions
  ): Promise<ApiResponse<T>> {
    const config = getApiConfig();
    return postFormDataWithXhr<T>(config.baseUrl, endpoint, formData, options);
  },

  async put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    const config = getApiConfig();
    return request<T>(config.baseUrl, endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  async patch<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    const config = getApiConfig();
    return request<T>(config.baseUrl, endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    const config = getApiConfig();
    return request<T>(config.baseUrl, endpoint, { method: 'DELETE' });
  },
};

export const h5pApiClient = {
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    const config = getApiConfig();
    return request<T>(config.h5pUrl, endpoint, { method: 'GET' });
  },

  async getRaw(endpoint: string): Promise<Response> {
    const config = getApiConfig();
    const url = `${config.h5pUrl}${endpoint}`;
    return fetch(url, {
      method: 'GET',
      headers: getH5pRequestHeaders(),
    });
  },
};
